import Cookies from 'js-cookie';
import actions from './actions';
import { DataService } from '../../config/dataService/dataService';
import HttpStatus from '../../utility/status.enum';
import Functions from '../../utility/functions';

const LoginAPIName = `/login`;
const LogoutAPIName = `/logout`;
const ForgotPasswordAPIName = `/forgot-password`;
const ResetPasswordAPIName = `/reset-password`;
const TermsConditionAndPolicyAPIName = `/get-terms-condition-privacy`;
const SwitchAccountAPIName = `/admin/switch-account`;
const SwitchBackAccountAPIName = `/admin/switch-back`;
const SetPasswordAPIName = `/onboarding/set-password`;

const {
  loginBegin,
  loginSuccess,
  loginErr,
  logoutBegin,
  logoutSuccess,
  logoutErr,
  forgotPasswordBegin,
  forgotPasswordErr,
  forgotPasswordSuccess,
  resetPasswordBegin,
  resetPasswordErr,
  resetPasswordSuccess,
  termsAndConditionBegin,
  termsAndConditionErr,
  termsAndConditionSuccess,
  setPasswordBegin,
  setPasswordSuccess,
  setPasswordErr,
} = actions;

const login = (values) => {
  return async (dispatch) => {
    dispatch(loginBegin());
    try {
      const response = await DataService.post(LoginAPIName, values);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(loginErr(response?.data?.message));
      } else {
        Cookies.set('access_token', response?.data?.result?.headerToken);
        Cookies.set('logedIn', true);
        localStorage.setItem('user', JSON.stringify(response?.data?.result));
        localStorage.setItem('cms_messages', JSON.stringify(response?.data?.result?.cms_messages));
        if (!localStorage.getItem('system-id')) {
          localStorage.setItem('system-id', Functions.generateSlug(20));
        }
        localStorage.setItem('permissions', JSON.stringify(response?.data?.result?.permissions));
        dispatch(loginSuccess(true)); 
      }
      return response?.data;
    } catch (err) {
      dispatch(loginErr(err));
    }
  };
};

const authOLogin = (values, callback) => {
  return async (dispatch) => {
    dispatch(loginBegin());
    Cookies.set('access_token', values.accessToken);
    Cookies.set('logedIn', true);
    dispatch(loginSuccess(true));
    callback();
  };
};

const register = (values) => {
  return async (dispatch) => {
    dispatch(loginBegin());
    try {
      const response = await DataService.post('/register', values);
      if (response.data.errors) {
        dispatch(loginErr('Registration failed!'));
      } else {
        dispatch(loginSuccess(false));
      }
    } catch (err) {
      dispatch(loginErr(err));
    }
  };
};

const logOut = (callback) => {
  return async (dispatch) => {
    dispatch(logoutBegin());
    try {
      const response = await DataService.get(LogoutAPIName);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(logoutErr(response?.data?.message));
      } else {
        Cookies.remove('logedIn');
        Cookies.remove('access_token');
        localStorage.removeItem('user');
        localStorage.removeItem('permissions');
        localStorage.removeItem('system-id');
        localStorage.removeItem('default_user');
        dispatch(logoutSuccess(false));
      }
      return response?.data;
    } catch (err) {
      dispatch(logoutErr(err));
    }
  };
};

const forgotPassword = (values, callback) => {
  return async (dispatch) => {
    dispatch(forgotPasswordBegin());
    try {
      const response = await DataService.post(ForgotPasswordAPIName, values);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(forgotPasswordErr(response?.data?.message));
      } else {
        dispatch(forgotPasswordSuccess(true));
      }
      return response?.data;
    } catch (err) {
      dispatch(forgotPasswordErr(err));
    }
  };
};

const resetPasswords = (values) => {
  return async (dispatch) => {
    dispatch(resetPasswordBegin());
    try {
      const response = await DataService.post(ResetPasswordAPIName, values);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(resetPasswordErr(response?.data?.message));
      } else {
        dispatch(resetPasswordSuccess(true));
      }
      return response?.data;
    } catch (err) {
      dispatch(resetPasswordErr(err));
    }
  };
};

const termsConditionAndPolicyAccept = (values) => {
  return async (dispatch) => {
    dispatch(termsAndConditionBegin());
    try {
      const response = await DataService.get(TermsConditionAndPolicyAPIName, values);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(termsAndConditionErr(response?.data?.message));
      } else {
        dispatch(termsAndConditionSuccess(true));
      }
      return response?.data;
    } catch (err) {
      dispatch(termsAndConditionErr(err));
    }
  };
};

const switchAccount = (values) => {
  return async (dispatch) => {
    dispatch(loginBegin()); // Dispatch a Redux action indicating the login process has started
    try {
      const response = await DataService.post(SwitchAccountAPIName, values); // Make a POST request to the 'SwitchAccountAPIName' endpoint with the provided values
      if (response?.data?.status !== HttpStatus.HTTP_OK) { // Check if the response status is NOT HTTP_OK (200)
        // If the status is not OK, dispatch a Redux action for login error
        // Pass the error message from the response
        dispatch(loginErr(response?.data?.message));
      } else {
        applyAuthSession({
          result: response?.data?.result, // API auth result
          setDefaultUser: true, // set user as default
          clearDefaultUser: false, // keep existing default user
          dispatch, // Redux dispatch
        });
      }
      return response?.data; // Return the data from the API response for further usage if needed
    } catch (err) {
      dispatch(loginErr(err)); // If the API call fails (network error, server error), dispatch a login error action
    }
  };
};

const switchBack = () => {
  return async (dispatch) => {
    dispatch(loginBegin()); // start login/switch-back process (set loading state)
    try {
      const response = await DataService.post(SwitchBackAccountAPIName); // call API to switch back to original account
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(loginErr(response?.data?.message)); // dispatch error if API response is not successful
      } else {
        applyAuthSession({
          result: response?.data?.result,// auth data returned from API
          setDefaultUser: false, // do not overwrite the default user
          clearDefaultUser: true, // clear impersonated/default user session
          dispatch, // update global auth state
        });
      }
      return response?.data;
    } catch (err) {
      dispatch(loginErr(err));
    }
  };
};

const applyAuthSession = ({ result, setDefaultUser = false, clearDefaultUser = false, dispatch }) => {
  // preserve default user only once (only for switchAccount)
  if (setDefaultUser && !localStorage.getItem('default_user')) {
    const currentUser = localStorage.getItem('user');
    if (currentUser) localStorage.setItem('default_user', currentUser);
  }
  // clear default user (only for switchBack or normal login/logout)
  if (clearDefaultUser) {
    localStorage.removeItem('default_user');
  }
  Cookies.set('access_token', result.headerToken);
  Cookies.set('logedIn', true); // Set a flag in cookies to indicate the user is logged in
  localStorage.setItem('user', JSON.stringify(result)); // Save the whole user object to localStorage
  localStorage.setItem('cms_messages', JSON.stringify(result?.cms_messages)); // Save CMS messages from the user object to localStorage
  if (!localStorage.getItem('system-id')) { // If a 'system-id' does not already exist in localStorage
    localStorage.setItem('system-id', Functions.generateSlug(20)); // Generate a unique slug and save it as 'system-id'
  }
  localStorage.setItem('permissions', JSON.stringify(result?.permissions)); // Save the user's permissions array/object to localStorage
  localStorage.setItem('auth_sync', String(Date.now()));
  dispatch(loginSuccess(true)); // Dispatch a Redux action indicating login was successful
  window.location.reload(); // Reload the browser window to reflect the new account session
};

const setPassword = (values) => {
  return async (dispatch) => {
    dispatch(setPasswordBegin());
    try {
      const response = await DataService.post(SetPasswordAPIName, values);
      if (response?.data?.status !== HttpStatus.HTTP_OK) {
        dispatch(setPasswordErr(response?.data?.message));
      } else {
        dispatch(setPasswordSuccess(true));
      }
      return response?.data;
    } catch (err) {
      dispatch(setPasswordErr(err));
    }
  };
};

export { login, authOLogin, logOut, register, forgotPassword, resetPasswords, termsConditionAndPolicyAccept, switchAccount, switchBack, setPassword };
